import {
  CRONTAB_DAY_OF_WEEK_TO_DAY_LETTER,
  DAY_OF_WEEK_ORDER,
} from '@/components/Schedules/constants';
import { EDayOfWeek } from '@/components/Schedules/types';
import { FormatScheduleItemType } from '@/types/schedules';
import {
  addAppDays,
  appWallClockParts,
  appWallClockToInstant,
  formatInAppTz,
} from '@/utils/spain-time';

export const getScheduleDays = (dayOfWeek: string) => {
  if (!dayOfWeek) return '';

  const days = ['D', 'L', 'M', 'X', 'J', 'V', 'S'];
  return dayOfWeek
    .split(',')
    .map(day => days[parseInt(day.trim(), 10) % 7])
    .join(', ');
};

export const getArrayOfDays = (daysOfWeek: string) => {
  if (!daysOfWeek) return [];
  return daysOfWeek.split(',') as unknown as EDayOfWeek[];
};

export function getScheduleFormatted(schedule: FormatScheduleItemType[]): string[] {
  return schedule.map(scheduleItem => {
    if (!scheduleItem.repetition_day_of_week) {
      return `${scheduleItem.start_hour} - ${scheduleItem.end_hour}`;
    }

    const daysString = getScheduleItemRepetitionDayOfWeekFormatted(scheduleItem);
    return `${daysString} ${scheduleItem.start_hour} - ${scheduleItem.end_hour}`.trim();
  });
}

export function getScheduleItemRepetitionDayOfWeekFormatted(
  scheduleItem: FormatScheduleItemType
): string {
  if (!scheduleItem.repetition_day_of_week) return '';
  const days = scheduleItem.repetition_day_of_week.split(',') as EDayOfWeek[];
  return getDaysOfWeekFormatted(days);
}

export function getDaysOfWeekFormatted(daysOfWeek: EDayOfWeek[]): string {
  const sortedDays = DAY_OF_WEEK_ORDER.filter(day => daysOfWeek.includes(day))
    .map(dayOfWeek => CRONTAB_DAY_OF_WEEK_TO_DAY_LETTER[dayOfWeek])
    .filter(Boolean);
  return sortedDays.join(',');
}

const MILLISECONDS_IN_MINUTE = 60000;

type TDatedEmission = {
  start_datetime: string;
  end_datetime: string;
};

/**
 * `2026-08-17T10:00:00+02:00` reads `10:00` — the Madrid hour — for every viewer,
 * wherever they are and whatever offset the value carries. An unreadable
 * datetime has no hour.
 */
function toWallClockHour(isoDatetime: string | undefined | null): string {
  const time = isoDatetime?.trim() ? new Date(isoDatetime.trim()).getTime() : NaN;

  return Number.isNaN(time) ? '' : formatInAppTz(time, 'HH:mm');
}

/**
 * The window to show for an emission — `10:00 - 11:00` — as the Madrid wall clock rather
 * than the viewer's clock.
 *
 * Both ends, because an emission is a closed window and the reviewer is placing the item
 * inside it. Same separator as `getScheduleFormatted`, which renders template slots.
 *
 * Falls back to the start alone when the end is missing or unreadable, so a half-usable
 * emission still says something rather than nothing.
 */
export function getProgramBroadcastHours(
  programBroadcast: TDatedEmission | null | undefined
): string {
  const start = toWallClockHour(programBroadcast?.start_datetime);
  if (!start) return '';

  const end = toWallClockHour(programBroadcast?.end_datetime);
  return end ? `${start} - ${end}` : start;
}

const MILLISECONDS_IN_DAY = 24 * 60 * MILLISECONDS_IN_MINUTE;

/** A programme schedule hour (`H:mm`, `HH:mm` or `HH:mm:ss`) as `HH:mm:ss`, or null if unreadable. */
function toScheduleHms(hour: string): string | null {
  const parts = /^(\d{1,2}):(\d{2})(?::(\d{2}))?$/.exec(hour.trim());

  if (!parts) return null;

  const [hours, minutes, seconds = '00'] = parts.slice(1);

  return `${hours.padStart(2, '0')}:${minutes}:${seconds}`;
}

/**
 * The instant of a programme schedule `hour` on a Madrid calendar day
 * (`yyyy-MM-dd`): schedule hours are Spanish wall clock for every viewer
 * (RCOMM-6264). An hour that cannot be read yields an Invalid Date.
 */
export function getScheduleHourOnAppDay(day: string, hour: string): Date {
  const hms = toScheduleHms(hour);

  return hms ? appWallClockToInstant(day, hms) : new Date(NaN);
}

/**
 * Builds a Date taking the day from `date` — its Madrid day, `dayOffset` days
 * later — and the time from a program schedule `hour` (format "HH:MM:SS") as
 * Madrid wall clock. If no hour is provided (the program has no schedule), the
 * original date is kept, moved by `dayOffset` whole days.
 */
export function applyScheduleHour(date: Date, hour?: string, dayOffset = 0): Date {
  if (!hour) return new Date(date.getTime() + dayOffset * MILLISECONDS_IN_DAY);

  return getScheduleHourOnAppDay(addAppDays(appWallClockParts(date).date, dayOffset), hour);
}
